import { Product, Variant } from "../../types/types";
import { getVariant } from "../../utils";
import { getColourPathParam } from "../../utils/routing/getColourPathParam";
import { getSizeQuery } from "../../utils/routing/getSizeQuery";
import { useRouter } from "next/router";
import { encodeSize } from "../../utils/string/encodeSize";

type Params = {
  colourParam: string | undefined;
  sizeParam: string | undefined;
  product: Product | undefined;
};

let prevParams: Params = {
  colourParam: undefined,
  sizeParam: undefined,
  product: undefined,
};

let currentVariant: Variant;
let isSelected: boolean;

export function useVariant(product: Product): {
  variant: Variant;
  isSelected: boolean;
} {
  const router = useRouter();
  const sizeParam = getSizeQuery(router.query);
  const colourParam = getColourPathParam(router.query);

  if (paramsHaveChanged({ colourParam, sizeParam, product })) {
    prevParams = { colourParam, sizeParam, product };
    currentVariant = getVariant(product, colourParam, sizeParam);
    isSelected = isVariantSelected(product, currentVariant, sizeParam);
  }

  return { variant: currentVariant, isSelected: isSelected };
}

function paramsHaveChanged({ colourParam, sizeParam, product }: Params) {
  return !(
    colourParam === prevParams.colourParam &&
    sizeParam === prevParams.sizeParam &&
    Object.is(product, prevParams.product)
  );
}

function isVariantSelected(
  product: Product,
  variant: Variant,
  sizeParam: string | undefined,
) {
  if (!selectedVariantHasAlternativeSizes(product, variant)) {
    return true;
  }
  if (selectedVariantSizeMatchesSizeParam(variant, sizeParam)) {
    return true;
  }
  return false;
}

function selectedVariantSizeMatchesSizeParam(
  variant: Variant,
  sizeParam: string | undefined,
) {
  return encodeSize(variant.differentiators?.size) === encodeSize(sizeParam);
}

function selectedVariantHasAlternativeSizes(
  product: Product,
  selectedVariant: Variant,
) {
  return (
    variantsByColour(product.variants, selectedVariant.differentiators?.colour)
      .length > 1
  );
}

function variantsByColour(
  variants: Variant[],
  colour: string | null | undefined,
) {
  return variants.filter(
    (variant) => variant.differentiators?.colour === colour,
  );
}
